
import PageLayout from 'PageLayout';
import { MoveLeft } from 'lucide-react';
import moment from 'moment';

import { getRole } from 'utils/routeUtility';
import { EButton, ECheckbox, EDateInput, EFormSection, EMultiSelect, ENumberInput, EPageTitle, ESelect } from 'libs/ui';

import useHotelMarkupForm from '../Hooks/useHotelMarkupForm';

export default function HotelMarkupForm() {

    const {
        navigate,
        hotelChargeApplyForDropdown,
        markupFormChargesDropdown,
        hotelCategoriesDropdown,
        hotelSuppliersDropdown,
        travelTypeDropdown,
        allCountryList,
        hotelPlaces,
        getHotelPlacesByCountries,

        handleChange,
        handleChangeMarkupChargeWithAmountTypes,
        markup,
        handleSaveMarkup
    } = useHotelMarkupForm();

    return (
        <PageLayout
            footer={
                <div className="flex justify-end gap-2 ">
                    <EButton
                        variant="outline"
                        disabled={markup.isLoading}
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </EButton>
                    <EButton
                        loading={markup.isLoading}
                        onClick={handleSaveMarkup}
                    >
                        {markup.id ? "Update" : "Save"}
                    </EButton>
                </div>
            }
        >
            <EPageTitle
                title={`Create Markup B2C - Hotel`}
                end={
                    <div className="flex justify-end gap-2">
                        <EButton
                            variant='outline'
                            leftSection={<MoveLeft />}
                            disabled={markup.isLoading}
                            onClick={() => navigate(-1)}
                        >
                            Go Back
                        </EButton>
                    </div>
                }
            />

            <div>
                <EFormSection
                    title={"Filters"}
                >
                    <ESelect
                        label="Apply for"
                        placeholder="Select"
                        data={hotelChargeApplyForDropdown.map((each) => ({
                            label: each.key,
                            value: String(each.value)
                        }))}
                        required={markup.rules.hotelChargeApplyFor === "YES"}
                        error={markup.errors.hotelChargeApplyFor}
                        value={markup.details.hotelChargeApplyFor}
                        onChange={(value) => handleChange("hotelChargeApplyFor", value)}
                    />

                    <EMultiSelect
                        label="Travel Type"
                        placeholder="Select"
                        data={travelTypeDropdown.map((each) => ({
                            label: each.key,
                            value: String(each.value)
                        }))}
                        required={markup.rules.hotelTypes === "YES"}
                        error={markup.errors.hotelTypes}
                        value={markup.details.hotelTypes}
                        onChange={(value) => handleChange("hotelTypes", value)}
                    />
                </EFormSection>

                <EFormSection
                    title="Markup Validity (Travel Date)"
                >
                    <EDateInput
                        label="Start Date"
                        placeholder="Select a date"
                        minDate={new Date()}
                        required={markup.rules.startDate === "YES"}
                        error={markup.errors.startDate}
                        value={markup.details.startDate ? new Date(markup.details.startDate) : null}
                        onChange={(value) => handleChange("startDate", value ? moment(value).format("YYYY-MM-DD") : null)}
                    />

                    <EDateInput
                        label="End Date"
                        placeholder="Select a date"
                        disabled={markup.details.startDate === ""}
                        minDate={markup.details.startDate ? new Date(markup.details.startDate) : undefined}
                        required={markup.rules.endDate === "YES"}
                        error={markup.errors.endDate}
                        value={markup.details.endDate ? new Date(markup.details.endDate) : null}
                        onChange={(value) => handleChange("endDate", value ? moment(value).format("YYYY-MM-DD") : null)}
                    />
                </EFormSection>

                <EFormSection
                    title="Gross Price Range"
                >
                    <ENumberInput
                        label="From Price"
                        placeholder="Enter price"
                        required={markup.rules.minimumGrossPrice === "YES"}
                        error={markup.errors.minimumGrossPrice}
                        value={markup.details.minimumGrossPrice ?? undefined}
                        onChange={(value) => handleChange("minimumGrossPrice", value || null)}
                    />

                    <ENumberInput
                        label="To Price"
                        placeholder="Enter price"
                        required={markup.rules.maximumGrossPrice === "YES"}
                        error={markup.errors.maximumGrossPrice}
                        value={markup.details.maximumGrossPrice ?? undefined}
                        onChange={(value) => handleChange("maximumGrossPrice", value || null)}
                    />
                </EFormSection>

                <EFormSection
                    title="Destination Country"
                >
                    <EMultiSelect
                        label="Country"
                        placeholder="Select"
                        data={allCountryList}
                        required={markup.rules.countryIds === "YES"}
                        error={markup.errors.countryIds}
                        value={markup.details.countryIds.map(String)}
                        onChange={(value) => {
                            handleChange("countryIds", value);
                            getHotelPlacesByCountries(value);
                        }}
                    />

                    <EMultiSelect
                        label="Place"
                        placeholder="Select"
                        data={hotelPlaces}
                        required={markup.rules.placeIds === "YES"}
                        error={markup.errors.placeIds}
                        value={markup.details.placeIds.map(String)}
                        onChange={(value) => handleChange("placeIds", value)}
                    />
                </EFormSection>

                <EFormSection
                    title="Supplier & Category"
                >
                    <EMultiSelect
                        label="Supplier"
                        placeholder="Select"
                        data={hotelSuppliersDropdown.map((each) => ({
                            label: each.key,
                            value: String(each.value)
                        }))}
                        required={markup.rules.hotelSuppliers === "YES"}
                        error={markup.errors.hotelSuppliers}
                        value={markup.details.hotelSuppliers.map(String)}
                        onChange={(value) => handleChange("hotelSuppliers", value)}
                    />

                    <EMultiSelect
                        label="Category"
                        placeholder="Select"
                        data={hotelCategoriesDropdown.map((each) => ({
                            label: each.key,
                            value: String(each.value)
                        }))}
                        required={markup.rules.hotelCategories === "YES"}
                        error={markup.errors.hotelCategories}
                        value={markup.details.hotelCategories.map(String)}
                        onChange={(value) => handleChange("hotelCategories", value)}
                    />
                </EFormSection>

                <EFormSection
                    fullWidth
                    title="Markup"
                >
                    <div className="flex flex-col gap-5">
                        {markup.details.markupChargeWithAmountTypes.map((each, index) => (
                            <div key={index + "_markup"} className="flex gap-6">
                                <ENumberInput
                                    min={0}
                                    label={each.markupFormCharge}
                                    placeholder="Enter price"
                                    value={each.amount}
                                    onChange={(value) => handleChangeMarkupChargeWithAmountTypes(index, "amount", value)}
                                />
                                <ESelect
                                    label="Fee Type"
                                    placeholder="Select"
                                    data={["FIXED", "PERCENTAGE"]}
                                    value={each.feeType}
                                    onChange={(value) => handleChangeMarkupChargeWithAmountTypes(index, "feeType", value)}
                                />
                                <div></div>
                            </div>
                        ))}

                        {
                            getRole().includes("AFFILIATE")
                                ?
                                <></>
                                :
                                <ECheckbox
                                    label="Exclusive GST?"
                                    checked={markup.details.gst}
                                    onChange={(e) => handleChange("gst", e.target.checked)}
                                />
                        }
                    </div>
                </EFormSection>
            </div>
        </PageLayout>
    );

}